import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

type SentryLike = {
  captureException?: (e: unknown, ctx?: Record<string, unknown>) => void;
};

function reportToSentry(error: Error, info: ErrorInfo) {
  const sentry = (window as unknown as { Sentry?: SentryLike }).Sentry;
  if (sentry?.captureException) {
    sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  } else if (import.meta.env.DEV) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportToSentry(error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center gap-4 p-10 text-center rounded-2xl glass border border-red-200 dark:border-red-500/30"
        >
          <div className="p-3 rounded-full bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400">
            <AlertTriangle size={28} aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Something went wrong
            </h3>
            <p className="mt-1 text-sm text-surface-500 dark:text-surface-400 max-w-md">
              {this.state.error.message || 'An unexpected error occurred. The error has been reported.'}
            </p>
          </div>
          <button
            type="button"
            onClick={this.reset}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
          >
            <RefreshCw size={16} /> Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
